import type { ReactElement } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import CookiePolicy from "./pages/CookiePolicy";
import ParquesPage from "./pages/Parques";
import ParquePage from "./pages/Parque";
import { parques, parquePath } from "./content/parques";

/** Latest ISO date in a list ("YYYY-MM-DD" sorts as text). */
const latest = (dates: string[]): string | undefined => [...dates].sort().at(-1);

/**
 * Single source of truth for this app's routes.
 *
 * Used by BOTH:
 *  - the client entry (src/main.tsx -> BrowserRouter)
 *  - the server entry (src/entry-server.tsx -> StaticRouter, used by
 *    scripts/prerender.mjs at build time)
 *
 * TO ADD A NEW ROUTE:
 *  1. Add an entry to `routes` below, ABOVE the catch-all "*" route.
 *  2. List every concrete URL that should get its own static HTML file in
 *     `prerender`. For static routes that is just the path itself
 *     (e.g. prerender: ["/parques"]). For dynamic routes (":slug") enumerate the
 *     real URLs (e.g. prerender: ["/parques/chingaza"]) — ideally by mapping
 *     over your content source rather than hardcoding.
 *  3. Give the route its own `head` (title / description / canonical). THIS
 *     MATTERS: without it the generated page inherits the homepage's <title>
 *     and canonicalises itself to "/", which silently cancels out the SEO
 *     benefit of prerendering. See RouteHead below for the dynamic-route form.
 *  4. Nothing else. `pnpm build` picks it up automatically and writes
 *     dist/<path>/index.html with fully rendered markup and its own <head>.
 *
 * Leave `prerender` undefined for a route that should not be statically
 * generated. The "*" catch-all is special-cased: it is rendered to
 * dist/404.html, which Vercel serves for any unmatched URL.
 */

/** Per-page <head> values swapped into the template by scripts/prerender.mjs. */
export interface RouteHead {
  title?: string;
  description?: string;
  /**
   * Absolute URL, or a site-root-relative path ("/parques"). Defaults to the
   * page's own URL, which is almost always what you want — so you only need to
   * set this for something unusual like a paginated page pointing at page 1.
   */
  canonical?: string;
  /**
   * Per-page social share image, overriding the sitewide og:image /
   * twitter:image (which otherwise stays the generic /social-preview.jpg set
   * in index.html). Only set this when the page has its own hero image — an
   * index/listing page should keep the generic default.
   */
  image?: { url: string; alt?: string };
}

export interface AppRoute {
  path: string;
  element: ReactElement;
  /** Concrete URLs to statically generate at build time. */
  prerender?: string[];
  /**
   * Head metadata for the generated page(s). Either a fixed object, or — for
   * dynamic routes where every URL needs its own title — a function receiving
   * the concrete URL being generated:
   *
   *   { path: "/parques/:slug",
   *     prerender: parques.map(p => `/parques/${p.slug}`),
   *     head: (url) => {
   *       const parque = parques.find(p => `/parques/${p.slug}` === url);
   *       return { title: parque.title,
   *                description: parque.metaDescription };
   *     } }
   *
   * Anything omitted falls back to the homepage defaults in index.html.
   */
  head?: RouteHead | ((url: string) => RouteHead | undefined);
  /**
   * Sitemap <lastmod> ("YYYY-MM-DD") for each generated URL. Omit it and the
   * sitemap falls back to the site-wide LAST_UPDATED_ISO.
   */
  lastmod?: (url: string) => string | undefined;
}

export const routes: AppRoute[] = [
  { path: "/", element: <Index /> , prerender: ["/"] },
  {
    path: "/parques",
    element: <ParquesPage />,
    prerender: ["/parques"],
    lastmod: () => latest(parques.map((p) => p.verificadoEl)),
    head: {
      title: "Parques naturales de Colombia: guías prácticas",
      description:
        "Tarifas, cómo llegar, horarios y reglas de los parques nacionales de Colombia más visitados, con datos del sitio oficial y fecha de revisión.",
    },
  },
  {
    // One static file per park, generated from src/content/parques.ts.
    path: "/parques/:slug",
    element: <ParquePage />,
    prerender: parques.map(parquePath),
    lastmod: (url) => parques.find((p) => parquePath(p) === url)?.verificadoEl,
    head: (url) => {
      const parque = parques.find((p) => parquePath(p) === url);
      if (!parque) return undefined;
      return {
        title: parque.title,
        description: parque.metaDescription,
        // 1200x630 JPG crop of the park's hero (public/og/), since social
        // apps don't reliably render the AVIF used on the page.
        image: { url: `/og/${parque.slug}.jpg`, alt: parque.fotos.portada.alt },
      };
    },
  },
  {
    path: "/politica-cookies",
    element: <CookiePolicy />,
    prerender: ["/politica-cookies"],
    head: {
      title: "Política de cookies | Camping Parque Tayrona",
      description:
        "Qué cookies usa Camping Parque Tayrona, para qué sirven y cómo puedes cambiar tus preferencias en cualquier momento.",
    },
  },
  /* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */
  { path: "*", element: <NotFound /> },
];

export interface PrerenderTarget {
  url: string;
  head?: RouteHead;
  lastmod?: string;
}

/**
 * Every URL the build should emit a static HTML file for, paired with the head
 * metadata resolved for that specific URL. Consumed by scripts/prerender.mjs.
 */
export const prerenderTargets: PrerenderTarget[] = routes.flatMap((route) =>
  (route.prerender ?? []).map((url) => ({
    url,
    head: typeof route.head === "function" ? route.head(url) : route.head,
    lastmod: route.lastmod?.(url),
  })),
);
